import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DealPriorityApiService } from '../services/deal-priority-api.service';
import {
  DealPriorityRequest,
  DealPriorityResponse,
} from '../models/deal-priority.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { PreventSpecialCharactersDirective } from '../../../shared/directives/prevent-special-characters.directive';
import { PreventLongNamesDirective } from '../../../shared/directives/prevent-long-names.directive';
import { PreventLongDescriptionsDirective } from '../../../shared/directives/prevent-long-descriptions.directive';
import { PreventLongOrdersDirective } from '../../../shared/directives/prevent-long-orders.directive';
import { ValidatorFormsInformation } from '../../../shared/utils/validator-forms-information.utils';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-deal-priority-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    PreventSpecialCharactersDirective,
    PreventLongNamesDirective,
    PreventLongDescriptionsDirective,
    PreventLongOrdersDirective,
  ],
  templateUrl: './deal-priority-form.component.html',
})
export class DealPriorityFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  itemId: number | null = null;

  // Form fields
  name = '';
  private originalName = '';
  description = '';
  priorityOrder: number | null = null;
  color = '';
  isActive = true;

  dealPrioritiesExisting: DealPriorityResponse[] = [];

  constructor(
    private api: DealPriorityApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.itemId = +id;
      this.cargar(this.itemId);
    }
    this.getDealPrioritiesExisting();
  }

  getDealPrioritiesExisting() {
    const businessId = this.authService.getBusinessId();
    if (!businessId) {
      this.error = 'No se identificó la empresa';
      return;
    }

    this.api.getAll(businessId).subscribe({
      next: (dealPriorities) => {
        this.dealPrioritiesExisting = dealPriorities.content ?? [];
      },
      error: (err) => {
        this.toast.error({
          title: 'Prioridades',
          message:
            err.error?.message || 'No se pudo obtener prioridades existentes',
        });
      },
    });
  }

  setError(message: string) {
    this.error = message;
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) {
      this.error = 'No se pudo obtener el negocio del usuario';
      this.loading = false;
      return;
    }

    this.api.getById(id, businessId).subscribe({
      next: (item) => {
        this.name = item.name;
        this.originalName = item.name;
        this.description = item.description || '';
        this.priorityOrder = item.priorityOrder ?? null;
        this.color = item.color || '';
        this.isActive = item.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la prioridad';
        this.loading = false;
      },
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Prioridad' : 'Nueva Prioridad';
  }

  guardar(): void {
    const nameValidation = ValidatorFormsInformation.validateText(
      this.name,
      'nombre',
      30,
    );
    if (nameValidation) {
      this.error = nameValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }
    const descriptionValidation = ValidatorFormsInformation.validateText(
      this.description,
      'descripción',
      500,
    );
    if (descriptionValidation) {
      this.error = descriptionValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const orderValidation = ValidatorFormsInformation.validateNumber(
      this.priorityOrder,
      'orden de prioridad',
      4,
    );

    if (orderValidation) {
      this.error = orderValidation;
      this.toast.warning({ title: 'Datos no válidos', message: this.error });
      return;
    }

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) {
      this.error = 'No se pudo obtener el negocio del usuario';
      this.toast.error({ title: 'Error', message: this.error });
      return;
    }

    if (
      this.dealPrioritiesExisting.some(
        (dealPriority) => dealPriority.priorityOrder === this.priorityOrder,
      )
    ) {
      this.error = 'La orden ya se encuentra en uso';
      this.toast.warning({ title: 'Orden duplicado', message: this.error });
      return;
    }
    if (
      this.dealPrioritiesExisting.some(
        (dealPriority) =>
          dealPriority.name.toLowerCase() === this.name.toLowerCase(),
      )
    ) {
      this.error = 'El nombre ya se encuentra en uso';
      this.toast.warning({ title: 'Nombre duplicado', message: this.error });
      return;
    }

    this.saving = true;
    this.error = '';

    const request: DealPriorityRequest = {
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      priorityOrder: this.priorityOrder ?? undefined,
      color: this.color || undefined,
      isActive: this.isActive,
      businessId,
    };

    if (this.isEditing && this.itemId) {
      this.api.update(this.itemId, request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/prioridades']);
          this.toast.updated('Prioridad',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar la prioridad';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    } else {
      this.api.create(request).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config-deals/prioridades']);
          this.toast.created('Prioridad', `Se creó "${this.name.trim()}" correctamente.`);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear la prioridad';
          this.toast.error({ title: 'Error', message: this.error });
        },
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-deals/prioridades']);
  }
}
